import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ModCard from '../components/ModCard';
import AnimatedBackground from '../components/AnimatedBackground';

const API_URL = process.env.REACT_APP_API_URL;

function MarketplacePage() {
  const [mods, setMods] = useState([]);
  const [filteredMods, setFilteredMods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);

  useEffect(() => {
    fetchMods();
  }, []);

  useEffect(() => {
    filterMods();
  }, [searchQuery, mods, selectedCategory, sortBy]);

  const fetchMods = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mods`);
      setMods(response.data);
      setFilteredMods(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching mods:', error);
      setLoading(false);
    }
  };

  const filterMods = () => {
    let filtered = [...mods];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(mod =>
        mod.name.toLowerCase().includes(query) ||
        mod.description.toLowerCase().includes(query) ||
        mod.category.toLowerCase().includes(query) ||
        (mod.tags && mod.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(mod => mod.category.toLowerCase() === selectedCategory.toLowerCase());
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // 'featured' - keep original order
        break;
    }

    setFilteredMods(filtered);
  };

  // Get unique categories from mods
  const categories = ['all', ...new Set(mods.map(mod => mod.category))];

  const handleSearch = () => {
    filterMods();
  };

  return (
    <div className="w-full relative">
      {/* Animated Background - Coding/Tech Theme */}
      <AnimatedBackground
        imageUrl="https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1920&h=1080&fit=crop"
        opacity={0.5}
      />

      {/* Hero Section */}
      <section className="relative w-full px-4 py-6 md:px-10 lg:px-20 z-10">
        <div className="relative w-full min-h-[70vh] flex items-center justify-center">
          <div className="relative z-10 flex flex-col items-center text-center max-w-4xl p-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 border border-green-500/50 bg-green-500/10 text-green-500 text-xs tracking-[0.2em] uppercase">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              System Online
            </div>

            <h1 className="text-6xl md:text-8xl lg:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-500 leading-[0.85] tracking-tighter mb-10"
              style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              StreetMoneyGaming<br/>
              <span className="text-green-500" style={{WebkitTextStroke: '1px #00ff41', color: 'transparent'}}></span>
            </h1>

            <div className="relative z-10 w-full max-w-[600px] mb-6">
              <div className="flex h-14 w-full items-stretch rounded-xl border border-white/10 bg-[#020403]/80 backdrop-blur-md shadow-2xl focus-within:border-green-500/50 transition-all hover:shadow-[0_0_20px_rgba(34,197,94,0.2)]">
                <div className="flex items-center justify-center pl-4 text-gray-400">
                  <span className="material-symbols-outlined">search</span>
                </div>
                <input
                  className="flex h-full w-full flex-1 bg-transparent px-4 text-white placeholder-gray-500 focus:outline-none text-base font-mono"
                  placeholder="Search for maps, cars, scripts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <div className="flex items-center pr-2">
                  <button
                    onClick={handleSearch}
                    className="h-10 rounded-lg bg-green-500 px-6 text-sm font-bold text-black transition-all hover:bg-white hover:shadow-[0_0_20px_#00ff41] uppercase tracking-widest"
                    style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)'}}>
                    SEARCH
                  </button>
                </div>
              </div>
            </div>

            <div className="text-gray-500 text-sm font-mono">
              {filteredMods.length} {filteredMods.length === 1 ? 'product' : 'products'} found
            </div>
          </div>
        </div>
      </section>

      {/* Scrolling Banner */}
      <div className="w-full bg-green-500 text-black py-2 overflow-hidden border-y border-green-500 shadow-[0_0_15px_#00ff41]">
        <div className="flex whitespace-nowrap gap-12 animate-[scroll_15s_linear_infinite] font-bold font-mono text-sm tracking-widest uppercase">
          <span>/// PREMIUM MODS AVAILABLE</span>
          <span>/// SECURE STRIPE PAYMENTS</span>
          <span>/// INSTANT DELIVERY</span>
          <span>/// 24/7 SUPPORT ACTIVE</span>
          <span>/// PREMIUM MODS AVAILABLE</span>
          <span>/// SECURE STRIPE PAYMENTS</span>
          <span>/// INSTANT DELIVERY</span>
          <span>/// 24/7 SUPPORT ACTIVE</span>
        </div>
      </div>

      {/* Main Content Grid */}
      <section className="flex flex-col px-4 md:px-10 lg:px-20 py-16 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 border-b border-white/10 pb-6">
          <div>
            <h2 className="text-blue-400 font-mono text-sm tracking-widest mb-2 flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">terminal</span> MODULE: STORE
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              AVAILABLE SERVER ADDONS
            </h3>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0 relative">
            {/* Filter Button */}
            <div className="relative">
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className={`p-2 rounded hover:bg-white/5 transition-colors border ${showFilterMenu ? 'bg-green-500/10 text-green-500 border-green-500' : 'text-gray-400 hover:text-green-500 border-white/10'}`}
                title="Filter by category"
              >
                <span className="material-symbols-outlined">filter_list</span>
              </button>

              {showFilterMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-[#0a110e] border border-green-500/30 shadow-2xl z-50 holo-card"
                  style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%)'}}>
                  <div className="p-2">
                    <div className="text-xs text-gray-400 font-mono uppercase tracking-wider px-3 py-2">Category</div>
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => {
                          setSelectedCategory(category);
                          setShowFilterMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm font-mono transition-all ${
                          selectedCategory === category
                            ? 'bg-green-500/20 text-green-500'
                            : 'text-gray-300 hover:bg-white/5 hover:text-green-500'
                        }`}
                      >
                        {category === 'all' ? 'All Categories' : category}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sort Button */}
            <div className="relative">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className={`p-2 rounded hover:bg-white/5 transition-colors border ${showSortMenu ? 'bg-green-500/10 text-green-500 border-green-500' : 'text-gray-400 hover:text-green-500 border-white/10'}`}
                title="Sort products"
              >
                <span className="material-symbols-outlined">sort</span>
              </button>

              {showSortMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-[#0a110e] border border-green-500/30 shadow-2xl z-50 holo-card"
                  style={{clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%)'}}>
                  <div className="p-2">
                    <div className="text-xs text-gray-400 font-mono uppercase tracking-wider px-3 py-2">Sort By</div>
                    <button
                      onClick={() => {
                        setSortBy('featured');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm font-mono transition-all ${
                        sortBy === 'featured'
                          ? 'bg-green-500/20 text-green-500'
                          : 'text-gray-300 hover:bg-white/5 hover:text-green-500'
                      }`}
                    >
                      Featured
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('price-low');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm font-mono transition-all ${
                        sortBy === 'price-low'
                          ? 'bg-green-500/20 text-green-500'
                          : 'text-gray-300 hover:bg-white/5 hover:text-green-500'
                      }`}
                    >
                      Price: Low to High
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('price-high');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm font-mono transition-all ${
                        sortBy === 'price-high'
                          ? 'bg-green-500/20 text-green-500'
                          : 'text-gray-300 hover:bg-white/5 hover:text-green-500'
                      }`}
                    >
                      Price: High to Low
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('rating');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm font-mono transition-all ${
                        sortBy === 'rating'
                          ? 'bg-green-500/20 text-green-500'
                          : 'text-gray-300 hover:bg-white/5 hover:text-green-500'
                      }`}
                    >
                      Highest Rated
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('name');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm font-mono transition-all ${
                        sortBy === 'name'
                          ? 'bg-green-500/20 text-green-500'
                          : 'text-gray-300 hover:bg-white/5 hover:text-green-500'
                      }`}
                    >
                      Name (A-Z)
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-green-500 font-mono animate-pulse">Loading digital assets...</div>
          </div>
        ) : filteredMods.length === 0 ? (
          <div className="flex flex-col justify-center items-center py-20 gap-4">
            <span className="material-symbols-outlined text-6xl text-gray-600">search_off</span>
            <div className="text-gray-400 font-mono">No products found matching "{searchQuery}"</div>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 px-6 py-2 bg-green-500 text-black font-bold rounded hover:bg-white transition-all"
            >
              Clear Search
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredMods.map((mod) => (
              <ModCard key={mod.id} mod={mod} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default MarketplacePage;
